<template>
  <div ref="rootRef" class="language-toggle" :class="{ open: isOpen }">
    <button
      type="button"
      class="language-trigger"
      :title="t('language.switch')"
      :aria-label="t('language.switch')"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      @click="toggleMenu"
    >
      <span class="language-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      </span>
      <span class="language-label">{{ currentLabel }}</span>
    </button>

    <Transition name="language-menu">
      <ul
        v-if="isOpen"
        class="language-menu"
        role="listbox"
        :aria-label="t('language.switch')"
      >
        <li
          v-for="option in options"
          :key="option.value"
          role="presentation"
        >
          <button
            type="button"
            class="language-option"
            role="option"
            :aria-selected="locale === option.value"
            :class="{ active: locale === option.value }"
            @click="selectLocale(option.value)"
          >
            <span>{{ option.label }}</span>
            <svg
              v-if="locale === option.value"
              class="option-check"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale, t } = useI18n()

const rootRef = ref(null)
const isOpen = ref(false)

const options = computed(() => ([
  { value: 'zh-CN', label: t('language.zh') },
  { value: 'en-US', label: t('language.en') },
]))

const currentLabel = computed(() => {
  return options.value.find((option) => option.value === locale.value)?.label ?? t('language.zh')
})

function toggleMenu() {
  isOpen.value = !isOpen.value
}

function closeMenu() {
  isOpen.value = false
}

function selectLocale(value) {
  if (locale.value !== value) {
    locale.value = value
    localStorage.setItem('locale', value)
  }
  closeMenu()
}

function handleDocumentClick(event) {
  if (!rootRef.value?.contains(event.target)) {
    closeMenu()
  }
}

function handleDocumentKeydown(event) {
  if (event.key === 'Escape') {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  document.addEventListener('keydown', handleDocumentKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
  document.removeEventListener('keydown', handleDocumentKeydown)
})
</script>

<style scoped>
.language-toggle {
  position: relative;
  flex-shrink: 0;
}

.language-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 10px;
  border: none;
  border-radius: var(--mc-radius-md);
  background: transparent;
  color: var(--mc-body);
  font-size: 13px;
  font-weight: 500;
  font-family: var(--mc-font-body);
  cursor: pointer;
}

.language-trigger:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--mc-accent) 45%, transparent);
  outline-offset: 2px;
}

.language-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--mc-muted);
}

.language-icon svg {
  width: 16px;
  height: 16px;
}

.language-label {
  line-height: 1;
  white-space: nowrap;
}

.language-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 200;
  min-width: 132px;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: var(--mc-canvas);
  border: 1px solid var(--mc-hairline);
  border-radius: var(--mc-radius-md);
  box-shadow: var(--mc-shadow-lg);
}

.language-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: calc(var(--mc-radius-md) - 2px);
  background: transparent;
  color: var(--mc-body);
  font-size: 13px;
  font-weight: 500;
  font-family: var(--mc-font-body);
  text-align: left;
  cursor: pointer;
}

.language-option.active {
  color: var(--mc-ink);
  background: color-mix(in srgb, var(--mc-surface-soft) 70%, var(--mc-canvas));
}

.option-check {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--mc-accent, var(--mc-ink));
}

.language-menu-enter-active,
.language-menu-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.language-menu-enter-from,
.language-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
